
import React from 'react';
import { TrendingUp, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ReportSection from '@/components/report/ReportSection';
import { getScoreText, getScoreColorClass } from '@/utils/propertyScoreCalculator';

interface InvestmentProjectionProps {
  investmentProjection: {
    valueIncrease: number;
    years: number;
    rentalReturn: number;
  };
  negotiationTips?: string[];
  score?: number;
  strengths?: string[];
  weaknesses?: string[];
}

const InvestmentProjection: React.FC<InvestmentProjectionProps> = ({ 
  investmentProjection = {
    valueIncrease: 0,
    years: 5,
    rentalReturn: 0
  }, 
  negotiationTips = [],
  score = 50,
  strengths = [],
  weaknesses = []
}) => {
  // Determina se o imóvel está acima ou abaixo da média do mercado
  // Valor positivo significa acima da média (desfavorável), negativo significa abaixo (favorável)
  const isAboveAverage = investmentProjection.valueIncrease > 0;
  const valueComparisonText = isAboveAverage 
    ? `Este imóvel está aproximadamente ${Math.abs(investmentProjection.valueIncrease).toFixed(1)}% acima da média do mercado para imóveis semelhantes no mesmo bairro.`
    : `Este imóvel está aproximadamente ${Math.abs(investmentProjection.valueIncrease).toFixed(1)}% abaixo da média do mercado para imóveis semelhantes no mesmo bairro.`;

  // Análise baseada no score
  const getInvestmentAnalysis = () => {
    if (score >= 80) {
      return "Este imóvel apresenta um bom potencial de investimento, com preço competitivo em relação ao mercado local e características que se destacam positivamente.";
    } else if (score >= 60) {
      return "Este imóvel está próximo das médias do bairro, mas possui algumas características que merecem atenção. Recomendamos avaliar detalhadamente os pontos fortes e fracos e comparar com mais imóveis.";
    } else {
      return "Este imóvel apresenta características que requerem cautela na avaliação como investimento. O preço pode estar acima da média do mercado ou há pontos fracos significativos a considerar.";
    }
  };

  // Gerar dicas de negociação baseadas no score, preço e análise
  const generateNegotiationTips = () => {
    if (negotiationTips && negotiationTips.length > 0) {
      return negotiationTips;
    }
    
    const tips = [];
    const priceDiff = investmentProjection.valueIncrease;
    
    // Dicas baseadas no preço
    if (priceDiff > 5) {
      tips.push(`O imóvel está ${Math.abs(priceDiff).toFixed(1)}% acima da média de preço do bairro. Utilize este dado para negociar uma redução entre 5% e 8%, mencionando exemplos comparativos de imóveis similares mais baratos.`);
    } else if (priceDiff < -5) {
      tips.push(`Mesmo com o preço ${Math.abs(priceDiff).toFixed(1)}% abaixo da média do mercado, avalie se há margem para uma negociação adicional de 3-5%, especialmente se observar necessidades de manutenção ou renovação.`);
    } else {
      tips.push(`Com preço alinhado à média do mercado (diferença de apenas ${Math.abs(priceDiff).toFixed(1)}%), foque em negociar benefícios adicionais como mobília, eletrodomésticos ou condições de pagamento mais vantajosas.`);
    }
    
    // Dicas baseadas no score
    if (score < 60) {
      tips.push("Durante a negociação, seja objetivo ao apontar os pontos fracos específicos do imóvel e sugira que o preço seja ajustado para refletir os investimentos necessários para corrigir esses problemas.");
    } else if (score > 80) {
      tips.push("Este imóvel tem alto potencial de valorização. Ao negociar, destaque que reconhece seus diferenciais, mas evite demonstrar ansiedade. Prepare sua documentação com antecedência para agilizar o processo caso seja necessário fechar rapidamente.");
    }
    
    // Dica baseada nos pontos fracos específicos
    if (weaknesses.length > 0) {
      const selectedWeakness = weaknesses[0];
      tips.push(`Aborde diretamente a questão "${selectedWeakness}" na negociação, calculando o custo aproximado para resolver este problema e usando esse valor como base para solicitar um desconto equivalente.`);
    }
    
    // Dica baseada nos pontos fortes específicos
    if (strengths.length > 0 && tips.length < 5) {
      tips.push(`Reconheça que "${strengths[0]}" é um diferencial valioso, mas equilibre essa observação destacando aspectos do imóvel que poderiam ser melhorados ou comparando com outros imóveis que oferecem benefícios semelhantes por valores menores.`);
    }
    
    // Dica adicional mais estratégica
    if (tips.length < 5) {
      tips.push("Pesquise o histórico do imóvel no mercado. Se estiver anunciado há mais de 90 dias, use isso como argumento para uma oferta mais baixa, indicando baixo interesse dos compradores no valor atual.");
    }
    
    return tips;
  };

  // Gerar as dicas de negociação
  const effectiveNegotiationTips = generateNegotiationTips();

  return (
    <ReportSection title="Projeção de Investimento" icon={<TrendingUp size={22} />}>
      <div className="p-4 bg-blue-dark/5 rounded-lg mb-6">
        <div className="flex items-start">
          <AlertCircle size={20} className="text-blue-dark mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-lg mb-1">Análise do Potencial do Investimento</h4>
            <p className="text-gray-700 mb-3">
              {getInvestmentAnalysis()}
            </p>
            <div className="flex items-center mt-2">
              <div className={`flex items-center ${isAboveAverage ? 'text-amber-500' : 'text-green-500'} font-medium`}>
                {isAboveAverage ? (
                  <ChevronUp size={20} className="mr-1" />
                ) : (
                  <ChevronDown size={20} className="mr-1" />
                )}
                <span>{Math.abs(investmentProjection.valueIncrease).toFixed(1)}%</span>
              </div>
              <span className="text-gray-600 ml-2">{valueComparisonText}</span>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center">
                <div className={`text-2xl font-bold ${getScoreColorClass(score)} mr-2`}>{score}</div>
                <div className="text-sm text-gray-700">/ 100</div>
                <div className="ml-3 px-2 py-0.5 bg-blue-light/10 text-blue-dark text-xs rounded-full">
                  {getScoreText(score)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <h3 className="text-lg font-medium mb-3">Dicas de Negociação</h3>
      <ul className="space-y-3">
        {effectiveNegotiationTips.map((tip, index) => (
          <li key={index} className="flex items-start">
            <span className="bg-blue-medium text-white rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5 flex-shrink-0">
              {index + 1}
            </span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </ReportSection>
  );
};

export default InvestmentProjection;
